/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API, useAuth } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Activity, Loader2, RefreshCw, Link2Off, Search, ShieldCheck,
  ShieldOff, AlertCircle, CheckCircle2, Clock,
} from 'lucide-react';

const STATUS_STYLE = {
  connected:       { cls: 'bg-emerald-100 text-emerald-800 border-emerald-300', label: 'Connected' },
  not_connected:   { cls: 'bg-stone-100 text-stone-700 border-stone-300',         label: 'Not connected' },
  pending_auth:    { cls: 'bg-sky-100 text-sky-800 border-sky-300',               label: 'Pending auth' },
  token_expired:   { cls: 'bg-amber-100 text-amber-800 border-amber-300',         label: 'Token expired' },
  error:           { cls: 'bg-rose-100 text-rose-800 border-rose-300',            label: 'Error' },
  revoked:         { cls: 'bg-stone-100 text-stone-700 border-stone-300',         label: 'Disconnected' },
};

export default function WearableIntegrationsAdmin() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const [integrations, setIntegrations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [whoopConfigured, setWhoopConfigured] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [intR, usersR] = await Promise.all([
        axios.get(`${API}/admin/wearables/integrations?provider=whoop`),
        axios.get(`${API}/users?role=subscriber`),
      ]);
      setIntegrations(intR.data?.integrations || []);
      setWhoopConfigured(!!intR.data?.whoop_configured);
      setUsers(usersR.data?.users || usersR.data || []);
    } catch (e) {
      toast.error('Could not load wearable integrations.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const byUser = useMemo(() => {
    const map = new Map();
    users.forEach(u => map.set(u.id, u));
    return map;
  }, [users]);

  const rows = useMemo(() => {
    // Union: every subscriber user + any integration row (even if user not in cache).
    const seen = new Set();
    const out = [];
    users.forEach(u => {
      const inte = integrations.find(i => (byUser.get(i.subscriber_id)?.id || i.subscriber_id) === u.id);
      out.push({ subscriber_id: u.id, name: u.name || u.email || u.id, email: u.email, integration: inte || null });
      seen.add(u.id);
    });
    integrations.forEach(i => {
      if (!seen.has(i.subscriber_id)) {
        out.push({ subscriber_id: i.subscriber_id, name: i.subscriber_id, email: '', integration: i });
      }
    });
    const q = search.trim().toLowerCase();
    return q
      ? out.filter(r => (r.name || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q))
      : out;
  }, [users, integrations, byUser, search]);

  const toggleAccess = async (subscriber_id, next) => {
    if (!isAdmin) return toast.error('Only admins can change wearable access.');
    setBusyId(subscriber_id);
    try {
      await axios.patch(`${API}/admin/wearables/${subscriber_id}/access`, { enabled: next });
      toast.success(next ? 'WHOOP access enabled.' : 'WHOOP access disabled.');
      await reload();
    } catch {
      toast.error('Could not update access.');
    } finally {
      setBusyId(null);
    }
  };

  const syncNow = async (subscriber_id) => {
    if (!isAdmin) return toast.error('Only admins can force-sync.');
    setBusyId(subscriber_id);
    try {
      const r = await axios.post(`${API}/admin/wearables/${subscriber_id}/sync?days=7`);
      toast.success(`Synced ${r.data?.days_synced ?? 0} day(s) for this subscriber.`);
      await reload();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(`Sync failed: ${(typeof d === 'object' ? d.message || d.code : d) || 'unknown'}`);
      await reload();
    } finally {
      setBusyId(null);
    }
  };

  const disconnect = async (subscriber_id) => {
    if (!isAdmin) return toast.error('Only admins can disconnect.');
    if (!window.confirm('Disconnect this subscriber from WHOOP? Historical signals are preserved.')) return;
    setBusyId(subscriber_id);
    try {
      await axios.delete(`${API}/wearables/disconnect/${subscriber_id}/whoop`);
      toast.success('Disconnected.');
      await reload();
    } catch {
      toast.error('Could not disconnect.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-wearable-integrations" className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-['Outfit'] font-light text-[#2C2A29]">Wearable Integrations</h1>
            <p className="text-sm text-[#8A8681] font-['Work_Sans'] mt-1">
              Manage per-subscriber WHOOP connections, enable access, trigger sync, or disconnect.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {whoopConfigured ? (
              <span data-testid="whoop-platform-configured" className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
                WHOOP · configured
              </span>
            ) : (
              <span data-testid="whoop-platform-not-configured" className="text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                WHOOP · awaiting credentials
              </span>
            )}
            <Button
              onClick={reload}
              variant="outline"
              className="border-[#D8D4CE] text-[#5C5955]"
              data-testid="admin-wearables-refresh-btn"
            >
              <RefreshCw className="w-3 h-3" /> Refresh
            </Button>
          </div>
        </div>

        <div className="mb-4 flex items-center gap-2">
          <Search className="w-4 h-4 text-[#8A8681]" strokeWidth={1.5} />
          <Input
            data-testid="admin-wearables-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="max-w-sm"
          />
        </div>

        {loading ? (
          <div className="rounded-xl border border-[#EBE8E3] bg-white p-8 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-[#EBE8E3] bg-white p-8 text-center text-[#8A8681]">
            No subscribers found.
          </div>
        ) : (
          <div className="rounded-xl border border-[#EBE8E3] bg-white overflow-hidden">
            <table data-testid="admin-wearables-table" className="w-full text-sm font-['Work_Sans']">
              <thead className="bg-[#F4F2EE] text-[11px] uppercase tracking-[0.12em] text-[#8A8681]">
                <tr>
                  <th className="px-4 py-3 text-left">Subscriber</th>
                  <th className="px-4 py-3 text-left">Access</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Last sync</th>
                  <th className="px-4 py-3 text-left">Last error</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const int = r.integration;
                  const st = int?.status || 'not_connected';
                  const style = STATUS_STYLE[st] || STATUS_STYLE.not_connected;
                  const enabled = !!int?.wearable_access_enabled;
                  return (
                    <tr
                      key={r.subscriber_id || `row-${idx}`}
                      data-testid={`admin-wearables-row-${r.subscriber_id}`}
                      className="border-t border-[#EBE8E3] hover:bg-[#FBFaf9]"
                    >
                      <td className="px-4 py-3 text-[#2C2A29]">
                        <div>{r.name}</div>
                        {r.email && <div className="text-[11px] text-[#8A8681]">{r.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          size="sm"
                          variant={enabled ? 'default' : 'outline'}
                          disabled={!isAdmin || busyId === r.subscriber_id}
                          onClick={() => toggleAccess(r.subscriber_id, !enabled)}
                          className={enabled
                            ? 'bg-emerald-700 hover:bg-emerald-800 text-white text-xs rounded-full px-3'
                            : 'border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-3'}
                          data-testid={`admin-wearables-access-toggle-${r.subscriber_id}`}
                        >
                          {enabled
                            ? <><ShieldCheck className="w-3 h-3" /> Enabled</>
                            : <><ShieldOff className="w-3 h-3" /> Disabled</>}
                        </Button>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-0.5 rounded-full border ${style.cls}`}>
                          {style.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-[#8A8681]">
                        {int?.last_successful_sync_at
                          ? new Date(int.last_successful_sync_at).toLocaleString()
                          : (int?.last_sync_at ? new Date(int.last_sync_at).toLocaleString() + ' (attempt)' : '—')}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-rose-700 max-w-[220px] truncate" title={int?.last_error || ''}>
                        {int?.last_error || '—'}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!isAdmin || busyId === r.subscriber_id || st !== 'connected'}
                            onClick={() => syncNow(r.subscriber_id)}
                            className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-3"
                            data-testid={`admin-wearables-sync-${r.subscriber_id}`}
                          >
                            {busyId === r.subscriber_id
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <RefreshCw className="w-3 h-3" />} Sync
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!isAdmin || !int || st === 'not_connected' || st === 'revoked'}
                            onClick={() => disconnect(r.subscriber_id)}
                            className="border-[#D8D4CE] text-[#5C5955] text-xs rounded-full px-3"
                            data-testid={`admin-wearables-disconnect-${r.subscriber_id}`}
                          >
                            <Link2Off className="w-3 h-3" /> Disconnect
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="mt-6 text-[11px] text-[#8A8681] font-['Work_Sans']">
          <Clock className="w-3 h-3 inline mr-1" />
          Tokens are encrypted at rest (Fernet). Historical signals are preserved after disconnect.
        </p>
      </div>
    </AdminLayout>
  );
}
